import React from 'react';
import { CreditCard, Star, Users, Shield, Mail, Coins } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';
import { Link } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const { theme } = useColorTheme();

  const SectionIcon = ({ icon: Icon, className }: { icon: any; className?: string }) => (
    <Icon className={cn("h-5 w-5", theme.secondary, className)} />
  );

  const tokenPackages = [
    { tokens: 10, price: 10 },
    { tokens: 25, price: 25 },
    { tokens: 50, price: 50 },
    { tokens: 100, price: 100 },
  ];

  const examples = [
    { rate: 5, duration: 5, total: 25 },
    { rate: 3, duration: 10, total: 30 },
    { rate: 8, duration: 3, total: 24 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Pricing</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Coins className="h-4 w-4" />
            <span>Simple Token-Based Pricing</span>
          </div>
          <p className="mt-4 text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            Nexbii uses a token system to book live video calls with creators.
            Users purchase tokens on the platform and use them to book calls with their favourite creators.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">

          {/* Token System Overview */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Coins} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">How Tokens Work</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                <div className={cn("rounded-lg p-4 text-center border", theme.accent)}>
                  <div className="text-3xl font-bold mb-1" style={{ color: 'inherit' }}>1</div>
                  <div className="text-sm font-medium">Token</div>
                  <div className="text-lg font-semibold mt-1">= $1 USD</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <CreditCard className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-sm font-medium text-blue-800">Stored in your Nexbii account</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <Star className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-sm font-medium text-green-800">Used to book creator interactions directly</div>
                </div>
              </div>
            </div>
          </section>

          {/* Buying Tokens */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={CreditCard} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Buying Tokens</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-6">Users can purchase tokens through the Nexbii platform.</p>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 sm:px-6 py-3 font-semibold text-gray-700 uppercase tracking-wider text-xs sm:text-sm">Tokens</th>
                      <th className="text-right px-3 sm:px-6 py-3 font-semibold text-gray-700 uppercase tracking-wider text-xs sm:text-sm">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tokenPackages.map((pkg) => (
                      <tr key={pkg.tokens} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900">{pkg.tokens} Tokens</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold text-gray-900">${Number(pkg.price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={cn("mt-4 p-4 rounded-lg border-l-4", theme.accent)}>
                <p className="font-medium">Tokens are credited instantly to your account after purchase.</p>
              </div>
            </div>
          </section>

          {/* Creator Pricing */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Creator Pricing</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Creators determine their own pricing for video calls. Each creator can configure:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-6">
                <li>Price per minute</li>
                <li>Minimum call duration</li>
                <li>Maximum call duration</li>
              </ul>
              <p className="mb-6">The total number of tokens required for a booking depends on these settings.</p>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">Example</h3>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 sm:px-6 py-3 font-semibold text-gray-700 uppercase tracking-wider text-xs sm:text-sm">Creator Price</th>
                      <th className="text-left px-3 sm:px-6 py-3 font-semibold text-gray-700 uppercase tracking-wider text-xs sm:text-sm">Duration</th>
                      <th className="text-right px-3 sm:px-6 py-3 font-semibold text-gray-700 uppercase tracking-wider text-xs sm:text-sm">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {examples.map((ex, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-900">{ex.rate} Tokens/min</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-700">{ex.duration} min</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold text-gray-900">{ex.total} tokens</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={cn("mt-4 p-4 rounded-lg", theme.accent)}>
                <p className="font-medium">The full price in tokens will always be shown before confirming a booking.</p>
              </div>
            </div>
          </section>

          {/* Platform Role */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Star} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Platform Role</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                Nexbii provides the platform that enables fans to book and interact with creators through live video calls.
                Creators set their own prices, and Nexbii facilitates the booking and payment process.
              </p>
            </div>
          </section>

          {/* Refunds */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Shield} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Refunds</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Refunds may be available in certain situations, such as:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Creator not joining the call</li>
                <li>Eligible cancellations made at least 24 hours before the scheduled call</li>
              </ul>
              <p>
                Please see our{' '}
                <Link to="/refund-policy" className={cn("font-medium underline", theme.secondary)}>
                  Refund Policy
                </Link>{' '}
                for full details.
              </p>
            </div>
          </section>

          {/* Secure Payments */}
          <section className="p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Mail} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Secure Payments</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p>
                Payments are processed securely through our payment partner. Tokens are delivered to your
                Nexbii account after a successful purchase.
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 Nexbii. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
